import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { getCart } from '@/store/ls/cart';
import { CartContext } from '@/store/context/cart';
import { CartTableString } from "./ModalBlockedObjects.styles";
import { getBlockedObjects } from '@/helpers/api';
import { getLSRegion } from '@/store/ls/region';
import { DEFAULT_REGION_ID } from './Header/Info';
import { IBlockedObjectResponse } from '@/types/api-responses';

interface IModalOrderProps {
  onClose: () => void;
  prepId: number;
}

export default function ModalBlockedObjects({ onClose, prepId }: IModalOrderProps) {
  const router = useRouter();
  const { setCart } = useContext(CartContext);
  const [objects, setObjects] = useState<IBlockedObjectResponse[]>([]);
  const [sendedOrderId] = useState<string | null>(null);
  const [openTimeworkDay, setOpenTimeworkDay] = useState<number | null>(null);

  useEffect(() => {
    const currentRegion = getLSRegion();
    getBlockedObjects(prepId, currentRegion?.id ?? DEFAULT_REGION_ID).then(setObjects);
  }, [prepId]);

  const onCloseHandler = () => {
    if (!sendedOrderId) {
      onClose();
    } else {
      setCart(getCart());
      router.push('/');
    }
  };

  const toggleTimework = (objId: number) => {
    setOpenTimeworkDay(openTimeworkDay === objId ? null : objId);
  };

  const getTodayWorkTime = (timework: IBlockedObjectResponse['timework']) => {
    const today = new Date().getDay();
    const todayIndex = today === 0 ? 6 : today - 1;
    const todayWork = timework[todayIndex];
    if (!todayWork.date_from) return 'уточните по телефону';
    const from = todayWork.date_from.substr(0, 5);
    const to = todayWork.date_to ? todayWork.date_to.substr(0, 5) : '';
    return `${from} - ${to}`;
  };

  const getDayName = (dayId: number) => {
    const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
    return days[dayId];
  };

  return (
      <Container>
        <CloseOrder onClick={onCloseHandler}>Закрыть</CloseOrder>
        {!sendedOrderId && (
            <>
              <Title>
                <b>Забронировать данный товар Вы можете в аптеках:</b>
              </Title>
              <div>
                <CartTableString className="first-table-string" key="заголовки">
                  <div>Аптека</div>
                  <div>Режим работы</div>
                </CartTableString>
                {objects.map((item) => {
                  const isOpen = openTimeworkDay === item.obj_id && !item.obj_all_day;
                  return (
                      <CartTableString key={item.obj_id} className={isOpen ? 'open' : ''}>
                        <div className="mboName">
                          <span>{item.obj_name}</span>
                          <small>{item.obj_address}</small>
                        </div>
                        <div className="mboWorkTime">
                          <WorkTimeLink onClick={() => toggleTimework(item.obj_id)}>
                            {item.obj_all_day ? (
                                'Круглосуточно'
                            ) : (
                                <>
                                  Сегодня: {getTodayWorkTime(item.timework)}{' '}
                                  <Arrow>{isOpen ? '▲' : '▼'}</Arrow>
                                </>
                            )}
                          </WorkTimeLink>
                        </div>
                        <div className="mboPhone">
                          <PhoneToPharmacy
                              href={`/pharmacies/${item.obj_id}`}
                              target="_blank"
                          >
                            ✆
                          </PhoneToPharmacy>
                        </div>
                        {isOpen && (
                            <TimeworkWrapper className="mboTimework">
                              <TimeworkTable>
                                <thead>
                                <tr>
                                  <th>День</th>
                                  <th>Время работы</th>
                                  <th>Обед</th>
                                </tr>
                                </thead>
                                <tbody>
                                {item.timework.map((day) => (
                                    <tr key={day.day_id}>
                                      <td>{getDayName(day.day_id)}</td>
                                      <td>
                                        {day.date_from ? (
                                            <>
                                              {day.date_from.substr(0, 5)} -{' '}
                                              {day.date_to?.substr(0, 5) || '-'}
                                            </>
                                        ) : (
                                            'Выходной'
                                        )}
                                      </td>
                                      <td>
                                        {day.lunch_from ? (
                                            <>
                                              {day.lunch_from.substr(0, 5)} -{' '}
                                              {day.lunch_to?.substr(0, 5) || '-'}
                                            </>
                                        ) : (
                                            '-'
                                        )}
                                      </td>
                                    </tr>
                                ))}
                                </tbody>
                              </TimeworkTable>
                            </TimeworkWrapper>
                        )}
                      </CartTableString>
                  );
                })}
              </div>
            </>
        )}
      </Container>
  );
}

const WorkTimeLink = styled.a`
  cursor: pointer;
  color: var(--green);
  text-decoration: underline;
  font-size: 14px;
  &:hover {
    color: var(--red);
  }
  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const Arrow = styled.span`
  display: inline-block;
  margin-left: 5px;
  font-size: 10px;
`;

const TimeworkWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const TimeworkTable = styled.table`
  border-collapse: collapse;
  font-size: 13px;
  width: auto;
  min-width: 300px;
  th,
  td {
    padding: 5px 8px;
    text-align: left;
    border: 1px solid #ddd;
    white-space: nowrap;
  }
  th {
    background-color: var(--color-bg-lightgray);
    font-weight: 600;
  }
  tr:nth-child(even) {
    background-color: #f9f9f9;
  }
  @media (max-width: 480px) {
    font-size: 12px;
    width: 100%;
    th,
    td {
      padding: 4px 6px;
    }
  }
`;

const CloseOrder = styled.a`
  position: absolute;
  right: 0;
  top: -40px;
  color: var(--color-text-invert);
  padding: 10px;
  @media (max-width: 480px) {
    color: var(--green);
    top: -6px;
  }
`;

const Container = styled.div`
  padding: 20px 30px;
  text-align: center;
  background-color: var(--color-bg);
  width: 900px;
  max-width: calc(100% - 80px);
  border-radius: 20px;
  color: var(--color-title);
  font-size: 16px;
  line-height: var(--line-height-normal);
  position: relative;
  white-space: normal;
  @media (max-width: 480px) {
    width: 100%;
    max-width: 100%;
    padding: 30px 20px;
  }
`;

const Title = styled.div`
  margin-bottom: 30px;
  & strong {
    font-weight: normal;
    font-family: var(--font-semibold);
  }
  @media (max-width: 480px) {
    margin-bottom: 20px;
    font-size: 14px;
  }
`;

const PhoneToPharmacy = styled.a`
  display: inline-block;
  font-family: var(--font-awesome);
  color: #9c8450;
  font-size: 2.4rem;
  &:hover {
    color: #9c8450 !important;
  }
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;